import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView, Alert, TouchableOpacity, Platform } from 'react-native';
import { Text, Card, ActivityIndicator, Divider, DataTable, Button, Chip } from 'react-native-paper';
import { useNavigation } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import DateTimePicker from '@react-native-community/datetimepicker';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const formatDateYMD = (d) => {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const ConsolidadoDiarioScreen = () => {
  const navigation = useNavigation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [consolidado, setConsolidado] = useState(null);
  const [fecha, setFecha] = useState(new Date().toISOString().split('T')[0]);
  const [showDatePicker, setShowDatePicker] = useState(false);

  useEffect(() => { loadConsolidado(); }, [fecha]);

  const loadConsolidado = async () => {
    try {
      setLoading(true);
      const data = await api.getConsolidadoDiario(fecha);
      setConsolidado(data);
    } catch (error) {
      console.error('Error:', error);
      Alert.alert('Error', 'No se pudo cargar el consolidado');
    } finally {
      setLoading(false);
    }
  };

  const handleEjecutarCierreAuto = async () => {
    Alert.alert('Cierre Automático', '¿Ejecutar cierre automático ahora para todos los vendedores y repartidores?', [
      { text: 'Cancelar', style: 'cancel' },
      {
        text: 'Ejecutar', onPress: async () => {
          try {
            const result = await api.ejecutarCierreAutomatico();
            Alert.alert('Completado', result.message || 'Cierre ejecutado');
            loadConsolidado();
          } catch (error) {
            Alert.alert('Error', error.message);
          }
        }
      }
    ]);
  };

  if (loading) {
    return (
      <View style={styles.loaderContainer}>
        <ActivityIndicator size="large" color="#0066cc" />
        <Text style={styles.loaderText}>Cargando consolidado...</Text>
      </View>
    );
  }

  const c = consolidado?.consolidado || {};

  return (
    <ScrollView style={styles.container}>
      <Text style={styles.title}>Consolidado Diario</Text>

      {/* Selector de fecha */}
      <View style={styles.fechaRow}>
        <TouchableOpacity
          style={styles.fechaInputBtn}
          onPress={() => setShowDatePicker(true)}
          activeOpacity={0.7}
        >
          <Ionicons name="calendar-outline" size={18} color="#0066cc" />
          <View style={{ flex: 1 }}>
            <Text style={styles.fechaInputLabel}>Fecha</Text>
            <Text style={styles.fechaInputValue}>{fecha}</Text>
          </View>
          <Ionicons name="chevron-down" size={16} color="#888" />
        </TouchableOpacity>
        <Button mode="contained" onPress={loadConsolidado} style={styles.fechaBtn} compact>
          Consultar
        </Button>
      </View>
      {showDatePicker && (
        <DateTimePicker
          value={new Date(fecha)}
          mode="date"
          display={Platform.OS === 'ios' ? 'inline' : 'default'}
          maximumDate={new Date()}
          onChange={(event, selectedDate) => {
            setShowDatePicker(Platform.OS === 'ios');
            if (selectedDate) setFecha(formatDateYMD(selectedDate));
          }}
        />
      )}

      {/* Resumen general */}
      <Card style={styles.card}>
        <Card.Content>
          <Text style={styles.sectionTitle}>
            <Ionicons name="analytics" size={16} color="#0066cc" /> Resumen General - {fecha}
          </Text>
          <View style={styles.resumenGrid}>
            <View style={styles.resumenItem}>
              <Text style={styles.resumenLabel}>Total Ventas</Text>
              <Text style={styles.resumenValue}>L. {(c.total_ventas || 0).toFixed(2)}</Text>
              <Text style={styles.resumenCount}>{c.cantidad_ventas || 0} ventas</Text>
            </View>
            <View style={styles.resumenItem}>
              <Text style={styles.resumenLabel}>Entregas</Text>
              <Text style={styles.resumenValue}>L. {(c.total_entregas || 0).toFixed(2)}</Text>
              <Text style={styles.resumenCount}>{c.cantidad_entregas || 0} entregadas</Text>
            </View>
            <View style={styles.resumenItem}>
              <Text style={styles.resumenLabel}>Devoluciones</Text>
              <Text style={[styles.resumenValue, { color: '#f44336' }]}>-L. {(c.total_devoluciones || 0).toFixed(2)}</Text>
              <Text style={styles.resumenCount}>{c.cantidad_devoluciones || 0}</Text>
            </View>
          </View>

          <View style={styles.netoRow}>
            <Text style={styles.netoLabel}>Total Neto del Día:</Text>
            <Text style={styles.netoValue}>L. {(c.total_neto || 0).toFixed(2)}</Text>
          </View>

          <Divider style={{ marginVertical: 10 }} />

          <Text style={styles.metodoTitle}>Recaudación por Método</Text>
          <View style={styles.metodoGrid}>
            <View style={styles.metodoItem}><Ionicons name="cash" size={16} color="#4caf50" /><Text style={styles.metodoLabel}>Efectivo</Text><Text style={styles.metodoVal}>L. {(c.monto_efectivo || 0).toFixed(2)}</Text></View>
            <View style={styles.metodoItem}><Ionicons name="card" size={16} color="#1976d2" /><Text style={styles.metodoLabel}>Tarjeta</Text><Text style={styles.metodoVal}>L. {(c.monto_tarjeta || 0).toFixed(2)}</Text></View>
            <View style={styles.metodoItem}><Ionicons name="swap-horizontal" size={16} color="#ff9800" /><Text style={styles.metodoLabel}>Transferencia</Text><Text style={styles.metodoVal}>L. {(c.monto_transferencia || 0).toFixed(2)}</Text></View>
            <View style={styles.metodoItem}><Ionicons name="time" size={16} color="#9c27b0" /><Text style={styles.metodoLabel}>Crédito</Text><Text style={styles.metodoVal}>L. {(c.monto_credito || 0).toFixed(2)}</Text></View>
          </View>

          <Divider style={{ marginVertical: 10 }} />

          <View style={styles.statusRow}>
            <Text>Cierres individuales: {consolidado?.total_cierres || 0}</Text>
            <View style={{ flexDirection: 'row' }}>
              <Chip mode="outlined" style={{ backgroundColor: '#ccffcc', marginRight: 4 }} textStyle={{ fontSize: 10 }}>{consolidado?.cierres_completados || 0} completados</Chip>
              <Chip mode="outlined" style={{ backgroundColor: '#fff0cc' }} textStyle={{ fontSize: 10 }}>{consolidado?.cierres_pendientes || 0} pendientes</Chip>
            </View>
          </View>
        </Card.Content>
      </Card>

      {/* Por vendedor */}
      {consolidado?.por_vendedor && consolidado.por_vendedor.length > 0 && (
        <Card style={styles.card}>
          <Card.Content>
            <Text style={styles.sectionTitle}>
              <Ionicons name="people" size={16} color="#1976d2" /> Por Vendedor
            </Text>
            <DataTable>
              <DataTable.Header>
                <DataTable.Title>Vendedor</DataTable.Title>
                <DataTable.Title numeric>Ventas</DataTable.Title>
                <DataTable.Title numeric>Vendido</DataTable.Title>
              </DataTable.Header>
              {consolidado.por_vendedor.map(v => (
                <DataTable.Row key={v.id}>
                  <DataTable.Cell>{v.nombre}</DataTable.Cell>
                  <DataTable.Cell numeric>{v.total_ventas}</DataTable.Cell>
                  <DataTable.Cell numeric>L. {(v.total_vendido || 0).toFixed(2)}</DataTable.Cell>
                </DataTable.Row>
              ))}
            </DataTable>
          </Card.Content>
        </Card>
      )}

      {/* Por repartidor */}
      {consolidado?.por_repartidor && consolidado.por_repartidor.length > 0 && (
        <Card style={styles.card}>
          <Card.Content>
            <Text style={styles.sectionTitle}>
              <Ionicons name="car" size={16} color="#e65100" /> Por Repartidor
            </Text>
            <DataTable>
              <DataTable.Header>
                <DataTable.Title>Repartidor</DataTable.Title>
                <DataTable.Title numeric>Entregas</DataTable.Title>
                <DataTable.Title numeric>Entregado</DataTable.Title>
              </DataTable.Header>
              {consolidado.por_repartidor.map(r => (
                <DataTable.Row key={r.id}>
                  <DataTable.Cell>{r.nombre}</DataTable.Cell>
                  <DataTable.Cell numeric>{r.total_entregas}</DataTable.Cell>
                  <DataTable.Cell numeric>L. {(r.total_entregado || 0).toFixed(2)}</DataTable.Cell>
                </DataTable.Row>
              ))}
            </DataTable>
          </Card.Content>
        </Card>
      )}

      {/* Cierres individuales */}
      {consolidado?.cierres_individuales && consolidado.cierres_individuales.length > 0 && (
        <Card style={styles.card}>
          <Card.Content>
            <Text style={styles.sectionTitle}>Cierres Individuales</Text>
            {consolidado.cierres_individuales.map(ci => (
              <View key={ci.id} style={styles.cierreIndRow}>
                <View>
                  <Text style={styles.cierreIndNombre}>{ci.usuario_nombre} ({ci.tipo})</Text>
                  <Text style={styles.cierreIndMonto}>L. {parseFloat(ci.total_neto || 0).toFixed(2)}</Text>
                </View>
                <Chip mode="outlined" style={{ backgroundColor: ci.estado === 'completado' ? '#ccffcc' : '#fff0cc', height: 26 }} textStyle={{ fontSize: 10 }}>
                  {ci.estado}
                </Chip>
              </View>
            ))}
          </Card.Content>
        </Card>
      )}

      {/* Ejecutar cierre automático manual */}
      {['admin', 'supervisor'].includes(user?.rol) && (
        <Button mode="contained" onPress={handleEjecutarCierreAuto} style={styles.autoBtn} icon="timer">
          Ejecutar Cierre Automático Ahora
        </Button>
      )}

      <View style={{ height: 30 }} />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5', padding: 10 },
  title: { fontSize: 22, fontWeight: 'bold', textAlign: 'center', marginBottom: 12 },
  fechaRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 12, gap: 8 },
  fechaInput: { flex: 1, backgroundColor: '#fff', marginRight: 8 },
  fechaInputBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#ccc',
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 8,
    gap: 8,
  },
  fechaInputLabel: { fontSize: 11, color: '#888' },
  fechaInputValue: { fontSize: 14, color: '#333', fontWeight: '600' },
  fechaBtn: { backgroundColor: '#0066cc' },
  card: { marginBottom: 12, elevation: 2 },
  sectionTitle: { fontSize: 16, fontWeight: 'bold', color: '#0066cc', marginBottom: 10 },
  resumenGrid: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 10 },
  resumenItem: { flex: 1, alignItems: 'center', backgroundColor: '#f9f9f9', padding: 10, borderRadius: 6, marginHorizontal: 3 },
  resumenLabel: { fontSize: 11, color: '#666' },
  resumenValue: { fontSize: 15, fontWeight: 'bold', marginTop: 4 },
  resumenCount: { fontSize: 10, color: '#999' },
  netoRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#e8f5e9', padding: 12, borderRadius: 8 },
  netoLabel: { fontSize: 16, fontWeight: 'bold' },
  netoValue: { fontSize: 22, fontWeight: 'bold', color: '#2e7d32' },
  metodoTitle: { fontSize: 14, fontWeight: 'bold', color: '#555', marginBottom: 8 },
  metodoGrid: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between' },
  metodoItem: { width: '48%', flexDirection: 'row', alignItems: 'center', padding: 8, backgroundColor: '#fafafa', borderRadius: 4, marginBottom: 6 },
  metodoLabel: { flex: 1, fontSize: 12, marginLeft: 6, color: '#555' },
  metodoVal: { fontWeight: 'bold', fontSize: 13 },
  statusRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  cierreIndRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#eee' },
  cierreIndNombre: { fontWeight: '600' },
  cierreIndMonto: { fontSize: 14, color: '#333' },
  autoBtn: { marginTop: 12, backgroundColor: '#ff9800' },
  loaderContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', paddingTop: 50 },
  loaderText: { marginTop: 10, color: '#666' },
});

export default ConsolidadoDiarioScreen;
